/**
 * FaultLog Data Transformers
 * Converts between parallel arrays (API format) and row objects (UI format)
 *
 * Critical for maintaining data integrity when updating FaultLog entities
 */

import { FaultLog } from "@/shared/models/FaultLog";
import { CustomerRow, DeviceRow, isCustomerRowEmpty, isDeviceRowEmpty } from "@/shared/models/FaultLogRows";
import { Utils } from "./Utils";

/**
 * Converts parallel customer arrays from FaultLog into row objects for UI display
 *
 * Handles:
 * - Unequal array lengths (takes longest array length)
 * - Null/undefined values (converts to empty strings)
 * - Trims all string values
 * - Filters out completely empty rows
 *
 * @example
 * Input FaultLog:
 *   customerName: ["ACME Corp", "TechCo"]
 *   customerCity: ["New York", "Boston"]
 *   customerShipTo: ["123 Main St", ""]
 *   customerPostCode: ["10001", "02101"]
 *   customerFlag: ["VIP", "Standard"]
 *
 * Output CustomerRow[]:
 *   [
 *     { flag: "VIP", name: "ACME Corp", city: "New York", shipTo: "123 Main St", postCode: "10001" },
 *     { flag: "Standard", name: "TechCo", city: "Boston", shipTo: "", postCode: "02101" }
 *   ]
 */
export const apiToCustomerRows = (fault: FaultLog): CustomerRow[] => {
  const flags = fault.customerFlag || [];
  const names = fault.customerName || [];
  const cities = fault.customerCity || [];
  const stateCodes = fault.customerStateCode || [];
  const shipTos = fault.customerShipTo || [];
  const demandTypes = fault.customerDemandType || [];
  const postCodes = fault.customerPostCode || [];
  const terminals = fault.terminal || [];
  const products = fault.product || [];
  const numOfDeliveries = fault.numOfDeliveries || [];
  const vesselIds = fault.vesselId || [];

  // Find the longest array length to handle uneven arrays
  const maxLength = Math.max(
    flags.length,
    names.length,
    cities.length,
    stateCodes.length,
    shipTos.length,
    demandTypes.length,
    postCodes.length,
    terminals.length,
    products.length,
    numOfDeliveries.length,
    vesselIds.length
  );

  // If no data at all, return empty array
  if (maxLength === 0) {
    return [];
  }

  const rows: CustomerRow[] = [];

  for (let i = 0; i < maxLength; i++) {
    const row: CustomerRow = {
      flag: (flags[i] || "").trim(),
      name: (names[i] || "").trim(),
      city: (cities[i] || "").trim(),
      stateCode: (stateCodes[i] || "").trim(),
      shipTo: (shipTos[i] || "").trim(),
      demandType: (demandTypes[i] || "").trim(),
      postCode: (postCodes[i] || "").trim(),
      terminal: (terminals[i] || "").trim(),
      product: (products[i] || "").trim(),
      numOfDeliveries: fault.numOfDeliveries ? fault.numOfDeliveries[i] : undefined,
      vesselId: (vesselIds[i] || "").trim(),
    };

    // Skip completely empty rows
    if (!isCustomerRowEmpty(row)) {
      rows.push(row);
    }
  }

  return rows;
};

/**
 * Converts customer row objects back into parallel arrays for API submission
 *
 * Handles:
 * - Removes completely empty rows
 * - Trims all values
 * - Maintains array order
 *
 * @example
 * Input CustomerRow[]:
 *   [
 *     { flag: "VIP", name: "ACME Corp", city: "New York", shipTo: "123 Main St", postCode: "10001" },
 *     { flag: "Standard", name: "TechCo", city: "Boston", shipTo: "", postCode: "02101" }
 *   ]
 *
 * Output (partial FaultLog):
 *   customerName: ["ACME Corp", "TechCo"]
 *   customerCity: ["New York", "Boston"]
 *   customerShipTo: ["123 Main St", ""]
 *   customerPostCode: ["10001", "02101"]
 *   customerFlag: ["VIP", "Standard"]
 */
export const customerRowsToApi = (
  rows: CustomerRow[]
): Pick<
  FaultLog,
  | "customerName"
  | "customerCity"
  | "customerStateCode"
  | "customerShipTo"
  | "customerDemandType"
  | "customerPostCode"
  | "customerFlag"
  | "terminal"
  | "product"
  | "numOfDeliveries"
  | "customerDemandType"
  | "vesselId"
> => {
  // Filter out empty rows and trim values
  const validRows = rows.filter((row) => !isCustomerRowEmpty(row));

  return {
    customerFlag: validRows.map((row) => row.flag.trim()),
    customerName: validRows.map((row) => row.name.trim()),
    customerCity: validRows.map((row) => row.city.trim()),
    customerStateCode: validRows.map((row) => row.stateCode.trim()),
    customerShipTo: validRows.map((row) => row.shipTo.trim()),
    customerDemandType: validRows.map((row) => row.demandType.trim()),
    customerPostCode: validRows.map((row) => row.postCode.trim()),
    terminal: validRows.map((row) => row.terminal.trim()),
    product: validRows.map((row) => row.product.trim()),
    numOfDeliveries: validRows.map((row) => row.numOfDeliveries || 0),
    vesselId: validRows.map((row) => row.vesselId.trim()),
  };
};

/**
 * Converts parallel device arrays from FaultLog into row objects for UI display
 *
 * @example
 * Input FaultLog:
 *   deviceId: ["DEV-001", "DEV-002", "DEV-003"]
 *   deviceType: ["Sensor", "Gateway", "Controller"]
 *
 * Output DeviceRow[]:
 *   [
 *     { id: "DEV-001", type: "Sensor" },
 *     { id: "DEV-002", type: "Gateway" },
 *     { id: "DEV-003", type: "Controller" }
 *   ]
 */
export const apiToDeviceRows = (fault: FaultLog): DeviceRow[] => {
  const ids = fault.deviceId || [];
  const types = fault.deviceType || [];

  const maxLength = Math.max(ids.length, types.length);

  if (maxLength === 0) {
    return [];
  }

  const rows: DeviceRow[] = [];

  for (let i = 0; i < maxLength; i++) {
    const row: DeviceRow = {
      id: (ids[i] || "").trim(),
      type: (types[i] || "").trim(),
    };

    // Skip completely empty rows
    if (!isDeviceRowEmpty(row)) {
      rows.push(row);
    }
  }

  return rows;
};

/**
 * Converts device row objects back into parallel arrays for API submission
 *
 * @example
 * Input DeviceRow[]:
 *   [
 *     { id: "DEV-001", type: "Sensor" },
 *     { id: "DEV-002", type: "Gateway" }
 *   ]
 *
 * Output (partial FaultLog):
 *   deviceId: ["DEV-001", "DEV-002"]
 *   deviceType: ["Sensor", "Gateway"]
 */
export const deviceRowsToApi = (rows: DeviceRow[]): Pick<FaultLog, "deviceId" | "deviceType"> => {
  const validRows = rows.filter((row) => !isDeviceRowEmpty(row));

  return {
    deviceId: validRows.map((row) => row.id.trim()),
    deviceType: validRows.map((row) => row.type.trim()),
  };
};

/**
 * Validates customer row data before saving
 * Returns array of validation error messages
 */
export const validateCustomerRows = (rows: CustomerRow[]): string[] => {
  const errors: string[] = [];
  const validRows = rows.filter((row) => !isCustomerRowEmpty(row));
  if (validRows.length === 0) {
    errors.push("At least one customer is required");
  }

  validRows.forEach((row, index) => {
    if (Utils.isEmpty(row.shipTo)) {
      errors.push(`Customer row ${index + 1}: Ship To is required`);
    }

    if (Utils.isEmpty(row.terminal)) {
      errors.push(`Customer row ${index + 1}: Terminal is required`);
    }
  });

  return errors;
};

/**
 * Validates device row data before saving
 * Returns array of validation error messages
 */
export const validateDeviceRows = (rows: DeviceRow[]): string[] => {
  const errors: string[] = [];
  if (rows.filter((row) => row.id && row.id.trim().length >= 4).length === 0) {
    errors.push("Please add at least one full deviceid");
    return errors;
  }

  const validRows = rows.filter((row) => !isDeviceRowEmpty(row));
  validRows.forEach((row, index) => {
    if (row.id && !row.type) {
      errors.push(`Device row ${index + 1}: Device Type is required when Device ID is provided`);
    }
    if (row.type && (!row.id || row.id.length < 4)) {
      errors.push(`Device row ${index + 1}: Full device ID is required when Device Type is provided`);
    }
  });

  return errors;
};
